import React, { useState } from "react";
import useAuth from "../../../hooks/useAuth";
import { signupAPI } from "../../../api/index";
import "./SignupModal.css";

const Reveal = ({ open, children }) => (
  <div className={`signup-reveal${open ? " is-open" : ""}`}>
    <div className="signup-reveal-inner">{children}</div>
  </div>
);

const SignupModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    phone: "",
    email: "",
    name: "",
    companyName: "",
    businessFile: null,
  });

  const { getUserInfo } = useAuth();
  const [errors, setErrors] = useState({});
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const [checkedUsername, setCheckedUsername] = useState(""); // 중복확인한 아이디 저장
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        businessFile: file,
      }));
      setSelectedFileName(file.name);
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      confirmPassword: "",
      phone: "",
      email: "",
      name: "",
      companyName: "",
      businessFile: null,
    });
    setErrors({});
    setIsUsernameAvailable(null);
    setCheckedUsername("");
    setSelectedFileName("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, "");

    // 길이에 따라 하이픈 추가
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
        7,
        11
      )}`;
    }
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // 휴대폰번호 필드인 경우 자동 포맷팅
    if (field === "phone") {
      processedValue = formatPhoneNumber(value);
    } else {
      // 다른 필드는 trim하지 않고 그대로 저장
      processedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue,
    }));

    // username이 변경되면 중복 확인 상태 초기화
    if (field === "username") {
      // 중복확인한 아이디와 현재 입력된 아이디가 다르면 초기화
      const trimmedValue = value.trim();
      if (checkedUsername && trimmedValue !== checkedUsername) {
        setIsUsernameAvailable(null);
        setCheckedUsername("");
      }
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleBlur = (field) => {
    // 휴대폰번호는 포맷팅을 유지하므로 trim하지 않음
    if (field === "phone") {
      return;
    }

    // 포커스가 벗어날 때 trim 적용
    const trimmedValue = formData[field].trim();

    setFormData((prev) => ({
      ...prev,
      [field]: trimmedValue,
    }));
  };

  const checkUsernameAvailability = async () => {
    const trimmedUsername = formData.username.trim();

    if (!trimmedUsername) {
      alert("아이디를 먼저 입력해주세요.");
      return;
    }

    // username 검증
    if (trimmedUsername.length < 8) {
      alert("아이디는 8자 이상이어야 합니다.");
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(trimmedUsername)) {
      alert("아이디는 영문과 숫자만 사용 가능합니다.");
      return;
    }

    try {
      const result = await getUserInfo(trimmedUsername);

      if (result.success && result.isAvailable) {
        // 아이디 사용 가능
        setIsUsernameAvailable(true);
        setCheckedUsername(trimmedUsername); // 중복확인한 아이디 저장
        alert("사용 가능한 아이디입니다.");
      } else {
        // 아이디 중복 또는 오류
        setIsUsernameAvailable(false);
        setCheckedUsername(""); // 중복확인 실패 시 초기화
        alert(result.message || "중복 확인 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("중복 확인 오류:", error);
      setIsUsernameAvailable(false);
      setCheckedUsername(""); // 오류 시 초기화
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // 모든 필드에 trim 적용 (휴대폰번호는 하이픈 제거)
    const trimmedData = {
      username: formData.username.trim(),
      password: formData.password.trim(),
      confirmPassword: formData.confirmPassword.trim(),
      phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
      email: formData.email.trim(),
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
    };

    // 필수 필드 검증
    if (!trimmedData.username) {
      newErrors.username = "아이디를 입력해주세요";
    } else if (trimmedData.username.length < 8) {
      newErrors.username = "아이디는 8자 이상이어야 합니다";
    } else if (!/^[a-zA-Z0-9]+$/.test(trimmedData.username)) {
      newErrors.username = "아이디는 영문과 숫자만 사용 가능합니다";
    } else if (isUsernameAvailable === null) {
      newErrors.username = "아이디 중복 확인을 해주세요";
    } else if (isUsernameAvailable === false) {
      newErrors.username = "중복된 아이디입니다. 다른 아이디를 사용해주세요";
    }

    if (!trimmedData.password) {
      newErrors.password = "비밀번호를 입력해주세요";
    } else if (trimmedData.password.length < 6) {
      newErrors.password = "비밀번호는 6자 이상이어야 합니다";
    }

    if (trimmedData.password !== trimmedData.confirmPassword) {
      newErrors.confirmPassword = "비밀번호를 다시 입력해주세요";
    }
    if (!trimmedData.name) {
      newErrors.name = "성함을 입력해주세요";
    }

    if (!trimmedData.phone) {
      newErrors.phone = "휴대폰번호를 입력해주세요";
    } else if (trimmedData.phone.length < 10) {
      newErrors.phone = "올바른 휴대폰번호를 입력해주세요";
    } else if (!/^01[0-9]/.test(trimmedData.phone)) {
      newErrors.phone = "010, 011, 012, 019로 시작하는 번호를 입력해주세요";
    }

    if (!trimmedData.email) {
      newErrors.email = "이메일을 입력해주세요";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedData.email)) {
      newErrors.email = "올바른 이메일 형식을 입력해주세요";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      // API 키값에 맞게 데이터 변환
      const apiData = {
        username: formData.username.trim(),
        password: formData.password.trim(),
        name: formData.name.trim(),
        phone: formData.phone.replace(/[^0-9]/g, ""), // 하이픈 제거
        email: formData.email.trim(),
        company: formData.companyName.trim() || undefined,
        business_license_url: formData.businessFile ? "https://..." : undefined, // 실제 파일 업로드 URL로 변경 필요
      };

      try {
        const result = await signupAPI(apiData);

        if (result === "") {
          alert("회원가입이 완료되었습니다!");
          handleClose();
        } else {
          alert(`회원가입 실패: ${result}`);
        }
      } catch (error) {
        console.error("회원가입 오류:", error);
        alert("회원가입 중 오류가 발생했습니다.");
      }
    }
  };

  if (!isOpen) return null;

  const showConfirm =
    (formData.username.trim().length > 0 && formData.password.trim().length > 0) ||
    Boolean(errors.confirmPassword);
  const showName =
    (showConfirm && formData.confirmPassword.trim().length > 0) ||
    Boolean(errors.name);
  const showPhone =
    (showName && formData.name.trim().length > 0) || Boolean(errors.phone);
  const showEmail =
    (showPhone && formData.phone.trim().length > 0) || Boolean(errors.email);
  const showCompany =
    showEmail && formData.email.trim().length > 0;
  const mismatch =
    showConfirm &&
    formData.confirmPassword.length > 0 &&
    formData.password !== formData.confirmPassword;

  return (
    <div className="signup-modal-overlay">
      <div
        className="signup-modal-element"
        role="dialog"
        aria-labelledby="signup-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="signup-modal-header">
          <h1 id="signup-modal-title" className="signup-modal-title">
            회원가입
          </h1>
          <button
            type="button"
            className="signup-modal-close"
            onClick={handleClose}
            aria-label="닫기"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M4.5 4.5 13.5 13.5M13.5 4.5 4.5 13.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <form className="signup-modal-form" onSubmit={handleSubmit}>
          <label className="signup-field">
            <span>아이디</span>
            <div
              className={`signup-input-row${
                formData.username.trim() ? " has-value" : ""
              }`}
            >
              <input
                type="text"
                value={formData.username}
                placeholder="아이디"
                autoComplete="username"
                onChange={(e) => handleInputChange("username", e.target.value)}
                onBlur={() => handleBlur("username")}
              />
              <button
                type="button"
                className="signup-check"
                onClick={checkUsernameAvailability}
              >
                중복확인
              </button>
            </div>
            {errors.username && <p className="signup-hint">{errors.username}</p>}
            {isUsernameAvailable === true && (
              <p className="signup-ok">사용 가능한 아이디입니다.</p>
            )}
            {isUsernameAvailable === false && !errors.username && (
              <p className="signup-hint">중복된 아이디입니다.</p>
            )}
          </label>

          <label className="signup-field">
            <span>비밀번호</span>
            <div
              className={`signup-input${
                formData.password.trim() ? " has-value" : ""
              }`}
            >
              <input
                type="password"
                value={formData.password}
                placeholder="비밀번호"
                autoComplete="new-password"
                onChange={(e) => handleInputChange("password", e.target.value)}
                onBlur={() => handleBlur("password")}
              />
            </div>
            {errors.password && <p className="signup-hint">{errors.password}</p>}
          </label>

          <Reveal open={showConfirm}>
            <label className="signup-field">
              <span>비밀번호 확인</span>
              <div
                className={`signup-input${
                  formData.confirmPassword.trim() ? " has-value" : ""
                }`}
              >
                <input
                  type="password"
                  value={formData.confirmPassword}
                  placeholder="비밀번호 확인"
                  autoComplete="new-password"
                  onChange={(e) =>
                    handleInputChange("confirmPassword", e.target.value)
                  }
                  onBlur={() => handleBlur("confirmPassword")}
                />
              </div>
              {(mismatch || errors.confirmPassword) && (
                <p className="signup-hint">
                  {mismatch
                    ? "비밀번호가 일치하지 않습니다"
                    : errors.confirmPassword}
                </p>
              )}
            </label>
          </Reveal>

          <Reveal open={showName}>
            <label className="signup-field">
              <span>성함</span>
              <div
                className={`signup-input${
                  formData.name.trim() ? " has-value" : ""
                }`}
              >
                <input
                  type="text"
                  value={formData.name}
                  placeholder="성함"
                  autoComplete="name"
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                />
              </div>
              {errors.name && <p className="signup-hint">{errors.name}</p>}
            </label>
          </Reveal>

          <Reveal open={showPhone}>
            <label className="signup-field">
              <span>휴대폰번호</span>
              <div
                className={`signup-input${
                  formData.phone.trim() ? " has-value" : ""
                }`}
              >
                <input
                  type="tel"
                  value={formData.phone}
                  placeholder="010-0000-0000"
                  autoComplete="tel"
                  maxLength={13}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  onBlur={() => handleBlur("phone")}
                />
              </div>
              {errors.phone && <p className="signup-hint">{errors.phone}</p>}
            </label>
          </Reveal>

          <Reveal open={showEmail}>
            <label className="signup-field">
              <span>이메일</span>
              <div
                className={`signup-input${
                  formData.email.trim() ? " has-value" : ""
                }`}
              >
                <input
                  type="email"
                  value={formData.email}
                  placeholder="email@example.com"
                  autoComplete="email"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                />
              </div>
              {errors.email && <p className="signup-hint">{errors.email}</p>}
            </label>
          </Reveal>

          <Reveal open={showCompany}>
            <label className="signup-field">
              <span>업체명(선택)</span>
              <div
                className={`signup-input${
                  formData.companyName.trim() ? " has-value" : ""
                }`}
              >
                <input
                  type="text"
                  value={formData.companyName}
                  placeholder="업체명"
                  onChange={(e) =>
                    handleInputChange("companyName", e.target.value)
                  }
                  onBlur={() => handleBlur("companyName")}
                />
              </div>
            </label>
          </Reveal>

          <button
            type="submit"
            className="signup-submit"
            disabled={isUsernameAvailable !== true}
          >
            회원가입
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupModal;
